
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2>{`I am a library author. Should I bundle `}<inlineCode parentName="h2">{`styled-components`}</inlineCode>{` with my library?`}</h2>
    <p>{`If you are a library author, we recommend that you should not bundle and ship `}<inlineCode parentName="p">{`styled-components`}</inlineCode>{` module with your library. There are two steps that you need to do to achieve this:`}</p>
    <ul>
      <li parentName="ul">{`Marking `}<inlineCode parentName="li">{`styled-components`}</inlineCode>{` as external in your package dependencies`}</li>
      <li parentName="ul">{`Removing `}<inlineCode parentName="li">{`styled-components`}</inlineCode>{` from your library bundle`}</li>
    </ul>
    <h3>{`Marking `}<inlineCode parentName="h3">{`styled-components`}</inlineCode>{` as external in your package dependencies`}</h3>
    <p>{`To do this, you will need to move it from `}<inlineCode parentName="p">{`dependencies`}</inlineCode>{` to `}<a parentName="p" {...{
        "href": "https://docs.npmjs.com/files/package.json#devdependencies"
      }}><inlineCode parentName="a">{`devDependencies`}</inlineCode></a>{`
and include it in the `}<a parentName="p" {...{
        "href": "https://docs.npmjs.com/files/package.json#peerdependencies"
      }}><inlineCode parentName="a">{`peerDependencies`}</inlineCode></a>{` list in your `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`  {
-   "dependencies" : {
+   "devDependencies" : {
      "styled-components": "^3.4.9"
    },
+   "peerDependencies" : {
+     "styled-components": ">= 3"
+   }
  }
`}</code></pre>
    <p>{`Moving `}<inlineCode parentName="p">{`styled-components`}</inlineCode>{` to `}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{` will guarantee that it wouldn't be installed along with your library (`}<inlineCode parentName="p">{`npm install`}</inlineCode>{` or `}<inlineCode parentName="p">{`yarn add`}</inlineCode>{` will ignore `}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{` when a library is installed).`}</p>
    <p>{`Adding `}<inlineCode parentName="p">{`styled-components`}</inlineCode>{` to `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` will signal your library consumers that `}<inlineCode parentName="p">{`styled-components`}</inlineCode>{` is not included with the library and they need to install it themselves.`}</p>
    <p>{`Also, note that in the `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` section the version string has been made a more permissive `}<inlineCode parentName="p">{`>= 3`}</inlineCode>{`. This allows future versions of styled-components to work automatically and you can simply narrow the range with a patch update to your library if a breaking change is eventually added.`}</p>
    <h3>{`Removing `}<inlineCode parentName="h3">{`styled-components`}</inlineCode>{` from your library bundle`}</h3>
    <p>{`If you are bundling your library before shipping it, make sure that you are not bundling `}<inlineCode parentName="p">{`styled-components`}</inlineCode>{` along with it. Here are some examples of how to do this with some popular module bundling tools:`}</p>
    <h4>{`With Microbundle`}</h4>
    <p>{`If you are using `}<a parentName="p" {...{
        "href": "https://github.com/developit/microbundle"
      }}>{`Microbundle`}</a>{`, it will handle this step automatically. Microbundle treats every dependency in the `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` list as external and excludes it from the build for you.`}</p>
    <h4>{`With Rollup.js`}</h4>
    <p>{`If you are using `}<a parentName="p" {...{
        "href": "https://rollupjs.org"
      }}>{`Rollup.js`}</a>{`, you should provide an `}<a parentName="p" {...{
        "href": "https://rollupjs.org/guide/en#big-list-of-options"
      }}><inlineCode parentName="a">{`external`}</inlineCode></a>{` option in your config:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`  export default {
    entry: "my-awesome-library.js",
+   external: [
+     "styled-components"
+   ]
  }
`}</code></pre>
    <p>{`Another approach is to use the `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/rollup-plugin-peer-deps-external"
      }}>{`rollup-plugin-peer-deps-external`}</a>{` plugin which will automatically add the `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` in the `}<inlineCode parentName="p">{`external`}</inlineCode>{` option array for you.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`+ import peerDepsExternal from 'rollup-plugin-peer-deps-external';

  export default {
    entry: "my-awesome-library.js",
+   plugins: [
+    // Preferably set as first plugin.
+    peerDepsExternal(),
+   ]
  }
`}</code></pre>
    <h4>{`With Webpack`}</h4>
    <p>{`If you are using `}<a parentName="p" {...{
        "href": "https://webpack.js.org"
      }}>{`Webpack`}</a>{`, you should provide an `}<a parentName="p" {...{
        "href": "https://webpack.js.org/configuration/externals/"
      }}><inlineCode parentName="a">{`externals`}</inlineCode></a>{` option in your config:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`  modules.export = {
    entry: "my-awesome-library.js",
+   externals: {
+     "styled-components": {
+       commonjs: "styled-components",
+       commonjs2: "styled-components",
+       amd: "styled-components",
+     },
+   },
  }
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`You can find more useful information on how to bundle a library with Webpack at `}<a parentName="p" {...{
          "href": "https://webpack.js.org/guides/author-libraries/"
        }}>{`"Authoring Libraries"`}</a>{` section of Webpack documentation.`}</p>
    </blockquote>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;